/* utils */
import { createMenuItem } from 'utils/navigation/helpers';
import DateFormatter from 'utils/date/DateFormatter';
import RoleUtils from 'utils/RoleUtil';
import { isChinaUser } from 'utils/ChinaUtil';
import TrainingStatusUtil from 'utils/TrainingStatusUtil';
import { isAccountSharingReadonlyMode, isReadonlyMode } from 'system/SystemUtil';
import ViewerPulseOxEnable from 'models/user/ViewerPulseOxEnable';
import UserBiometrics from 'models/user/ViewerUserBiometrics';
import { garminTrailsHash, trailsHash } from 'services/omt/subscription/subscriptionFields';
import { MFPLinkStatus } from 'reducers/mfp/MFPLinkStatus';

/* routes */
import { BPPageRoute } from 'BloodPressure/BloodPressureConstant';
import { bodyBatteryRoute, ecgRoute, garminTrailsRoute, jetLagRoute } from 'routes/obfuscatedRoutes';

const buildMainMenuSection = ({ preferences, mfp, usageIndicators, advancedReporting, userType }) => {
    const dateObj = new Date();
    const today = DateFormatter.formatISODate(new Date());

    const buildHealthStatsItems = () => [
        createMenuItem({
            title: 'sleep',
            translationNamespace: 'common',
            url: `/sleep/${today}`,
            urlKeyword: 'sleep',
            condition: !!userType.isWellness
        }),
        createMenuItem({
            title: 'menu_weight',
            translationNamespace: 'widgets',
            url: '/weight',
            urlKeyword: 'weight'
        }),
        createMenuItem({
            title: 'blood_pressure',
            translationNamespace: 'bloodPressure',
            url: BPPageRoute,
            urlKeyword: 'pressure'
        }),
        createMenuItem({
            title: 'label_hydration',
            translationNamespace: 'hydration',
            url: `/hydration/${today}/0`,
            urlKeyword: 'hydration',
            condition: !!preferences.showHydration
        }),
        createMenuItem({
            title: mfp.linkStatus === MFPLinkStatus.ACTIVE ? 'title_calories_widget' : 'title_calories_no_mfp_widget',
            translationNamespace: 'widgets',
            url: `/calories/${today}`,
            urlKeyword: 'calories',
            condition: preferences.hasCaloriesAvailablePref === 'true'
        }),
        createMenuItem({
            title: 'pulse_ox',
            translationNamespace: 'device',
            url: `/pulse-ox/${today}`,
            condition:
                !!preferences.showAllDayPulseOx &&
                (ViewerPulseOxEnable.get('capableEnable') === true || UserBiometrics.get('locale') === 'ja') &&
                !!preferences.hasPulseOxAllDayCapableDevice
        }),
        createMenuItem({
            title: 'title_pulse_ox_acclimation_widget',
            translationNamespace: 'widgets',
            url: '/pulse-ox-acclimation',
            condition:
                !!preferences.hasPulseOxAllDayCapableDevice &&
                (ViewerPulseOxEnable.get('capableEnable') === true || UserBiometrics.get('locale') === 'ja')
        }),
        createMenuItem({
            title: 'label_respiration',
            translationNamespace: 'units',
            url: `/respiration/${today}`,
            urlKeyword: 'respiration',
            condition: !!preferences.showRespiration && !!preferences.hasRespirationCapableDevice
        }),
        createMenuItem({
            title: 'label_heart_rate',
            translationNamespace: 'units',
            url: `/heart-rate/${today}`,
            urlKeyword: 'heart-rate',
            condition: !!userType.isWellness
        }),
        createMenuItem({
            title: 'fitness_age',
            translationNamespace: 'fitness_age',
            url: '/fitness-age',
            urlKeyword: 'fitness-age',
            condition: !!preferences.hasFitnessAgeV2CapableDevice
        }),
        createMenuItem({
            title: 'title_stress_widget',
            translationNamespace: 'widgets',
            url: `/stress/${today}/0`,
            urlKeyword: 'stress',
            condition: !!userType.isWellness
        }),
        createMenuItem({
            title: 'body_battery',
            translationNamespace: 'bodybattery',
            url: `/${bodyBatteryRoute}`,
            urlKeyword: 'body-battery',
            condition: !!userType.isWellness && !!preferences.hasBodyBatteryCapableDevice
        }),
        createMenuItem({
            title: 'label_menstrual_cycle',
            translationNamespace: 'menstrual-cycle-tracking',
            url: `/menstrual-cycle-tracking/${today}`,
            condition: !!preferences.showMenstrualCycleTracking
        }),
        createMenuItem({
            title: 'label_pregnancy',
            translationNamespace: 'menstrual-cycle-tracking',
            url: `/pregnancy-detail/${today}`,
            condition: !!preferences.showPregnancy
        }),
        createMenuItem({
            title: 'ecg',
            translationNamespace: 'ecg',
            url: `/${ecgRoute}`,
            disabled: isReadonlyMode(),
            condition: !!preferences.showECG
        }),
        createMenuItem({
            title: 'health_snapshot',
            translationNamespace: 'activity',
            url: '/health-snapshots',
            condition: !!preferences.showHealthSnapshot
        })
    ];
    const buildTrainingAndPlanningItems = () => [
        createMenuItem({
            title: 'workouts',
            translationNamespace: 'workout',
            url: '/workouts',
            urlKeyword: 'workout'
        }),
        createMenuItem({
            title: preferences.dswEnabled ? 'training_menu_item_nav' : 'title_training_plan_widget', // Clean up ticket: CA-161362
            translationNamespace: 'widgets',
            url: '/training-plan'
        }),
        createMenuItem({
            title: 'events_menu_item_nav',
            translationNamespace: 'widgets',
            url: '/event-dashboard'
        }),
        createMenuItem({
            title: 'course_widget_title',
            translationNamespace: 'course',
            url: '/courses',
            disabled: isAccountSharingReadonlyMode()
        }),
        createMenuItem({
            title: 'pacepro_menu_item_nav',
            translationNamespace: 'widgets',
            url: '/pacepro'
        }),
        createMenuItem({
            title: 'power_guidance_menu_item_nav',
            translationNamespace: 'common',
            url: '/power-guidance',
            condition: !!preferences.hasPowerGuidanceCapableDevice
        }),
        createMenuItem({
            title: 'segments',
            translationNamespace: 'common',
            url: '/segments',
            disabled: isAccountSharingReadonlyMode()
        }),
        createMenuItem({
            title: garminTrailsHash,
            translationNamespace: trailsHash, // TODO: remove when feature is released
            url: `/${garminTrailsRoute}`,
            disabled: isAccountSharingReadonlyMode(),
            condition: preferences.isTrailEnabled && !isChinaUser()
        }),
        createMenuItem({
            title: 'course_map_label_popularity_heatmap',
            translationNamespace: 'course',
            url: '/popularity-heatmap',
            condition: !isChinaUser()
        }),
        createMenuItem({
            title: 'jet_lag_advisor',
            translationNamespace: 'jet_lag',
            url: `/${jetLagRoute}/manage-trips`,
            condition: !!preferences.showJetLag
        })
    ];
    const buildGolfItems = () => [
        createMenuItem({
            title: 'scorecards',
            translationNamespace: 'golf_main',
            url: '/my-scorecards',
            urlKeyword: 'scorecard',
            disabled: isAccountSharingReadonlyMode()
        }),
        createMenuItem({
            title: 'title_golf_stats_widget',
            translationNamespace: 'widgets',
            url: '/golf-stats',
            urlKeyword: 'golf-stat'
        }),
        createMenuItem({
            title: 'title_golf_course_widget',
            translationNamespace: 'widgets',
            url: '/my-golf-courses',
            urlKeyword: 'golf-course'
        }),
        createMenuItem({
            title: 'title_golf_leaderboard_widget',
            translationNamespace: 'widgets',
            url: '/golf-leaderboards',
            urlKeyword: 'golf-leaderboard',
            disabled: isAccountSharingReadonlyMode()
        })
    ];
    const buildWellnessActivities = () => [
        createMenuItem({
            title: 'activity_type_all',
            translationNamespace: 'activity_types',
            url: '/activities'
        }),
        createMenuItem({
            title: 'activity_type_steps',
            translationNamespace: 'activity_types',
            url: '/steps',
            urlKeyword: 'steps',
            condition: !!preferences.showSteps
        }),
        createMenuItem({
            title: 'label_floors_climbed_unit',
            translationNamespace: 'units',
            url: '/floors',
            urlKeyword: 'floors',
            condition: !preferences.hideFloorFeature
        }),
        createMenuItem({
            title: 'intensity_minutes',
            translationNamespace: 'daily_summary',
            url: '/intensity-minutes',
            urlKeyword: 'intensity-minutes'
        }),
        createMenuItem({
            title: 'activity_type_wheelchair_pushes',
            translationNamespace: 'activity_types',
            url: '/pushes',
            urlKeyword: 'pushes',
            condition: !!preferences.showWheelchairPushes
        })
    ];
    const buildPerformanceStatsItems = () => [
        createMenuItem({
            title: 'training_status_nav',
            translationNamespace: 'widgets',
            url: '/report/-1/all/current',
            condition: !!TrainingStatusUtil.isTrainingStatusAvailable(usageIndicators)
        }),
        createMenuItem({
            title: 'training_readiness_nav',
            translationNamespace: 'widgets',
            url: '/training-readiness/' + dateObj.toISOString().substring(0, 10),
            condition: !!usageIndicators.hasCurrentOrHistoricalIndicator('device', 'hasTrainingReadinessCapableDevice')
        }),
        createMenuItem({
            title: 'hrv_status_nav',
            translationNamespace: 'widgets',
            url: '/hrv-status',
            condition: !!usageIndicators.hasCurrentOrHistoricalIndicator('device', 'hasHrvStatusCapableDevice')
        }),
        createMenuItem({
            title: 'race_predictor_nav',
            translationNamespace: 'widgets',
            url: '/report/-29/running/current',
            condition: !!usageIndicators.hasCurrentOrHistoricalIndicator('device', 'hasRacePredictionsRunCapableDevice')
        }),
        createMenuItem({
            title: 'vo2_max_nav',
            translationNamespace: 'widgets',
            url: '/report/21/all/current',
            condition:
                !!usageIndicators.hasCurrentOrHistoricalIndicator('device', 'hasVO2MaxRunCapable') ||
                !!usageIndicators.hasCurrentOrHistoricalIndicator('device', 'hasVO2MaxBikeCapable')
        }),
        createMenuItem({
            title: 'RUNNING_ECONOMY',
            translationNamespace: 'reportswidget',
            icon: '',
            url: '/report/-49/running/current',
            condition:
                !!usageIndicators.hasCurrentOrHistoricalIndicator('device', 'hasRunningEconomyCapableDevice') &&
                preferences.isEconomyEnabled
        }),
        createMenuItem({
            title: 'label_training_effect',
            translationNamespace: 'units',
            url: '/report/62/running/last_seven_days',
            condition:
                !!usageIndicators.hasCurrentOrHistoricalIndicator('device', 'hasAerobicTrainingEffectCapableDevice') ||
                !!usageIndicators.hasCurrentOrHistoricalIndicator('device', 'hasAnaerobicTrainingEffectCapableDevice')
        }),
        createMenuItem({
            title: 'power_curve_title',
            translationNamespace: 'notification',
            url: '/report/-6/cycling/last_four_weeks',
            condition: !!usageIndicators.hasCurrentOrHistoricalIndicator('device', 'hasPowerCurveCapableDevice')
        }),
        createMenuItem({
            title: 'ftp',
            translationNamespace: 'power_guidance',
            url: '/report/50/all/current',
            condition: !!usageIndicators.hasCurrentOrHistoricalIndicator('device', 'hasFTPCapable')
        }),
        createMenuItem({
            title: 'critical_swim_speed_nav',
            translationNamespace: 'widgets',
            url: '/report/-37/swimming/current',
            condition: !!usageIndicators.hasCurrentOrHistoricalIndicator('device', 'hasCriticalSwimSpeedCapableDevice')
        }),
        createMenuItem({
            title: 'RUNNING_TOLERANCE',
            translationNamespace: 'reportswidget',
            icon: '',
            url: '/report/-46/running/current',
            condition:
                !!usageIndicators.hasCurrentOrHistoricalIndicator('device', 'hasRunningToleranceCapableDevice') &&
                preferences.isToleranceEnabled
        }),
        createMenuItem({
            title: 'lactate_threshold_label',
            translationNamespace: 'units',
            url: '/report/-39/running/current',
            condition: !!usageIndicators.hasCurrentOrHistoricalIndicator(
                'device',
                'hasRunningLactateThresholdCapableDevice'
            )
        }),
        createMenuItem({
            title: 'endurance_score_nav',
            translationNamespace: 'widgets',
            url: '/report/-41/all/current',
            condition: !!usageIndicators.hasCurrentOrHistoricalIndicator(
                'device',
                'hasHillScoreAndEnduranceScoreCapableDevice'
            )
        }),
        createMenuItem({
            title: 'hill_score_nav',
            translationNamespace: 'widgets',
            url: '/report/-40/running/last_one_day',
            condition: !!usageIndicators.hasCurrentOrHistoricalIndicator(
                'device',
                'hasHillScoreAndEnduranceScoreCapableDevice'
            )
        }),
        createMenuItem({
            title: 'hrv_stress_nav',
            translationNamespace: 'widgets',
            url: '/report/65/all/last_seven_days',
            condition: !!usageIndicators.hasCurrentOrHistoricalIndicator('device', 'hasHRVStressCapableDevice')
        })
    ];
    const performanceDashboardItems = () => {
        let subMenus = [];

        if (preferences.isGCPEnabled && advancedReporting) {
            advancedReporting.advancedSubMenus.forEach((item) => {
                subMenus.push(
                    createMenuItem({
                        title: item.name,
                        translationNamespace: '',
                        url: `/performance-dashboard/${item.dashboardId}`,
                        urlKeyword: item.dashboardId,
                        label: item.name
                    })
                );
            });

            if (advancedReporting.advancedSubMenus.length < advancedReporting.dashboardsLimit) {
                subMenus.push(
                    createMenuItem({
                        title: 'add_dashboard',
                        translationNamespace: 'main_container',
                        url: '/performance-dashboard',
                        urlKeyword: `advanced-edit`,
                        isActionItem: true
                    })
                );
            }
        }

        return subMenus;
    };

    let performanceItems = buildPerformanceStatsItems();

    return [
        createMenuItem({
            title: 'events_menu_item_nav',
            translationNamespace: 'widgets',
            url: '/event-dashboard',
            condition: !!userType.isRaceEventProvider
        }),
        createMenuItem({
            title: 'course_widget_title',
            translationNamespace: 'course',
            url: '/courses',
            disabled: isAccountSharingReadonlyMode(),
            condition: !!userType.isRaceEventProvider
        }),
        createMenuItem({
            title: 'activities',
            translationNamespace: 'userprofile',
            icon: preferences.isWheelchairMode ? 'activities-wheelchair' : 'activities',
            url: '/activities',
            condition: !userType.isWellness && (userType.isFitness || userType.isDive)
        }),
        createMenuItem({
            title: 'activities',
            translationNamespace: 'userprofile',
            icon: preferences.isWheelchairMode ? 'activities-wheelchair' : 'activities',
            menuItems: buildWellnessActivities(),
            condition: userType.isWellness
        }),
        createMenuItem({
            title: 'health_stats',
            translationNamespace: 'main_container',
            icon: 'heart',
            menuItems: buildHealthStatsItems(),
            condition: userType.isFitness || userType.isWellness || userType.isDive
        }),
        createMenuItem({
            title: 'performance_stats_nav',
            translationNamespace: 'widgets',
            uiIcon: 'ChartsBar',
            menuItems: performanceItems,
            condition: performanceItems.flat().length > 0
        }),
        createMenuItem({
            title: 'golf',
            translationNamespace: 'group',
            icon: preferences.isWheelchairMode ? 'activity-golf-wheelchair' : 'activity-golf',
            menuItems: buildGolfItems(),
            condition: userType.isOutdoor
        }),
        createMenuItem({
            title: 'training_and_planning',
            translationNamespace: 'main_container',
            icon: 'workouts',
            menuItems: [
                createMenuItem({
                    title: 'queue.workout',
                    translationNamespace: 'send_to_device',
                    url: '/workouts',
                    urlKeyword: 'workout'
                }),
                createMenuItem({
                    title: 'jet_lag_advisor',
                    translationNamespace: 'jet_lag',
                    url: `/${jetLagRoute}/manage-trips`,
                    condition: !!preferences.showJetLag
                })
            ],
            condition: !(userType.isFitness || userType.isDive) && userType.isWellness
        }),
        createMenuItem({
            title: 'training_and_planning',
            translationNamespace: 'main_container',
            icon: 'workouts',
            menuItems: buildTrainingAndPlanningItems(),
            condition: userType.isFitness || userType.isDive
        }),
        createMenuItem({
            title: 'title_diving_widget',
            translationNamespace: 'widgets',
            icon: 'activity-diving',
            url: '/diving',
            condition: RoleUtils.isDiveUserOnly() || userType.isDive
        }),
        createMenuItem({
            title: 'label_gear',
            translationNamespace: 'gear_tracking',
            icon: 'steps',
            url: '/gear',
            condition: userType.isFitness || userType.isWellness || userType.isDive || userType.isOutdoor
        }),
        createMenuItem({
            title: 'title_insights_widget',
            translationNamespace: 'widgets',
            icon: 'insights',
            url: '/insights',
            condition: userType.isWellness && !!preferences.showInsights
        }),
        createMenuItem({
            title: 'title_reports_widget',
            translationNamespace: 'widgets',
            icon: 'reports-bar',
            url: '/report',
            urlKeyword: 'reports',
            condition: userType.isFitness || userType.isWellness || userType.isDive
        }),
        createMenuItem({
            title: 'main_page_title',
            translationNamespace: 'performance_dashboard',
            icon: 'charts-dashboard',
            menuItems: performanceDashboardItems(),
            condition: preferences.isGCPEnabled
        })
    ].flat();
};

export default buildMainMenuSection;
